import { useEffect, useRef, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { ClipboardList, CreditCard, CalendarDays, Pill, Check, ChevronRight, Lock, PartyPopper, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import StepCompletionEffect from "@/components/StepCompletionEffect";
import { toast } from "sonner";

const PLAN_MAP: Record<string, string> = {
  "prod_UKAhiaNLwZ5KAl": "Monthly Membership",
  "prod_UKAhVJzNA9kEQX": "Annual Membership",
  "prod_V2Tq89xvGqjyJh": "Core Membership",
  "prod_UKdt0iublEBLXE": "Metabolic Support Add-On",
  "prod_UKdu7a6YtFjhMf": "Energy & Cellular Support Add-On",
  "prod_UKeEc1n1JvBWvr": "Recovery & Performance Add-On",
  "prod_UKeE3hIH14CJmg": "Hormonal Optimization Add-On",
};

interface JourneyStep {
  id: string;
  label: string;
  description: string;
  icon: React.ElementType;
  complete: boolean;
  actionPath?: string;
  actionLabel?: string;
  locked?: boolean;
}

const PatientProgressBar = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [steps, setSteps] = useState<JourneyStep[]>([]);
  const [loading, setLoading] = useState(true);
  const [newlyCompleted, setNewlyCompleted] = useState<Set<string>>(new Set());
  const prevStepsRef = useRef<Record<string, boolean>>({});
  const celebrationShownRef = useRef(false);

  useEffect(() => {
    if (!user) return;

    const fetchProgress = async () => {
      const [assessmentRes, subscriptionRes, appointmentRes, prescriptionRes] = await Promise.all([
        supabase.from("health_assessments").select("id").eq("user_id", user.id).limit(1),
        supabase.functions.invoke("check-subscription"),
        supabase.from("appointments").select("id, status").eq("patient_id", user.id).in("status", ["scheduled", "completed"]).limit(1),
        supabase.from("prescriptions").select("id").eq("patient_id", user.id).limit(1),
      ]);

      const hasAssessment = (assessmentRes.data?.length ?? 0) > 0;
      const hasSubscription = subscriptionRes.data?.subscribed === true;
      const productId = subscriptionRes.data?.product_id;
      const planName = productId ? PLAN_MAP[productId] || "Your Plan" : null;
      const hasConsultation = (appointmentRes.data?.length ?? 0) > 0;
      const hasPrescription = (prescriptionRes.data?.length ?? 0) > 0;

      setSteps([
        {
          id: "assessment",
          label: "Assessment",
          description: "Complete your health profile",
          icon: ClipboardList,
          complete: hasAssessment,
          actionPath: "/get-started",
          actionLabel: "Start Assessment",
        },
        {
          id: "payment",
          label: hasSubscription ? "Plan Activated" : "Activate Plan",
          description: hasSubscription
            ? planName || "Your membership is active"
            : hasAssessment
              ? "Choose a membership plan"
              : "Complete assessment first",
          icon: CreditCard,
          complete: hasSubscription,
          actionPath: "/customize",
          actionLabel: hasSubscription ? undefined : "Choose Plan",
          locked: !hasAssessment,
        },
        {
          id: "consultation",
          label: "Consultation",
          description: hasSubscription
            ? "Meet with your provider"
            : "Activate a plan to unlock",
          icon: CalendarDays,
          complete: hasConsultation,
          actionPath: "/book",
          actionLabel: "Book Consultation",
          locked: !hasSubscription,
        },
        {
          id: "treatment",
          label: hasPrescription ? "Treatment Received" : "Treatment",
          description: hasPrescription
            ? "Your prescription is on file"
            : hasConsultation
              ? "Your provider will send a prescription if appropriate"
              : "Complete your consultation first",
          icon: Pill,
          complete: hasPrescription,
          locked: !hasConsultation && !hasPrescription,
        },
      ]);

      // Detect newly completed steps
      const freshlyDone = new Set<string>();
      const prev = prevStepsRef.current;
      if (Object.keys(prev).length > 0) {
        if (hasAssessment && !prev["assessment"]) freshlyDone.add("assessment");
        if (hasSubscription && !prev["payment"]) freshlyDone.add("payment");
        if (hasConsultation && !prev["consultation"]) freshlyDone.add("consultation");
        if (hasPrescription && !prev["treatment"]) freshlyDone.add("treatment");
      }
      prevStepsRef.current = {
        assessment: hasAssessment,
        payment: hasSubscription,
        consultation: hasConsultation,
        treatment: hasPrescription,
      };

      if (freshlyDone.size > 0) {
        setNewlyCompleted(freshlyDone);
        setTimeout(() => setNewlyCompleted(new Set()), 1200);
      }

      // Celebration toast when all 4 steps are complete
      const allComplete = hasAssessment && hasSubscription && hasConsultation && hasPrescription;
      if (allComplete && !celebrationShownRef.current) {
        celebrationShownRef.current = true;
        setTimeout(() => {
          toast("🎉 Journey Complete!", {
            description: "Congratulations — you've completed all steps. Your personalized therapy is on its way!",
            icon: <PartyPopper className="h-5 w-5 text-primary" />,
            duration: 6000,
          });
        }, 600);
      }

      setLoading(false);
    };

    fetchProgress();
  }, [user]);

  if (loading) return null;

  const currentIndex = steps.findIndex((s) => !s.complete);
  const completedCount = steps.filter((s) => s.complete).length;
  const allDone = currentIndex === -1;
  const incompleteSteps = steps.filter((s) => !s.complete);
  const hasTreatmentButMissingSteps = steps.find((s) => s.id === "treatment")?.complete && incompleteSteps.length > 0;

  return (
    <div className="mb-10">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display text-lg text-foreground">Your Journey</h2>
        <span className="text-xs text-muted-foreground font-medium">
          {completedCount}/{steps.length} complete
        </span>
      </div>

      {/* Progress line */}
      <div className="w-full h-1.5 bg-muted rounded-full mb-6 overflow-hidden">
        <div
          className="h-full bg-primary rounded-full transition-all duration-500"
          style={{ width: `${allDone ? 100 : (completedCount / steps.length) * 100}%` }}
        />
      </div>

      {hasTreatmentButMissingSteps && (
        <div className="flex items-start gap-3 p-3 mb-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 dark:bg-amber-950/30 dark:border-amber-800 dark:text-amber-200">
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <div className="text-xs leading-relaxed">
            <span className="font-semibold">Complete your care journey</span> — You've received treatment, but finishing the remaining steps ({incompleteSteps.map(s => s.label).join(", ")}) ensures uninterrupted access to refills, provider support, and follow-up care.
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {steps.map((step, index) => {
          const isComplete = step.complete;
          const isCurrent = index === currentIndex;
          const isLocked = step.locked && !isComplete;
          const StepIcon = step.icon;

          return (
            <Card
              key={step.id}
              className={`relative overflow-hidden transition-all group ${
                isLocked
                  ? "border-border bg-card opacity-40 cursor-not-allowed"
                  : isCurrent
                  ? "border-primary bg-primary/5 shadow-md ring-1 ring-primary/20 cursor-pointer"
                  : isComplete
                  ? "border-primary/30 bg-primary/5 cursor-pointer"
                  : "border-border bg-card opacity-60 cursor-pointer"
              }`}
              onClick={() => {
                if (isLocked) return;
                if ((isCurrent || isComplete) && step.actionPath) navigate(step.actionPath);
              }}
            >
              <StepCompletionEffect active={newlyCompleted.has(step.id)} />
              <div className="p-4">
                {/* Icon */}
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center mb-3 ${
                    isComplete
                      ? "bg-primary text-primary-foreground"
                      : isLocked
                      ? "bg-muted text-muted-foreground"
                      : isCurrent
                      ? "bg-primary/10 text-primary ring-2 ring-primary ring-offset-2 ring-offset-background"
                      : "bg-muted text-muted-foreground"
                  }`}
                >
                  {isComplete ? (
                    <Check
                      size={18}
                      strokeWidth={3}
                      className={newlyCompleted.has(step.id) ? "" : ""}
                      style={
                        newlyCompleted.has(step.id)
                          ? { animation: "check-pop 0.5s ease-out forwards" }
                          : undefined
                      }
                    />
                  ) : isLocked ? (
                    <Lock size={18} />
                  ) : (
                    <StepIcon size={18} />
                  )}
                </div>

                {/* Label */}
                <p className={`text-sm font-semibold mb-0.5 ${
                  isComplete ? "text-primary" : isCurrent ? "text-foreground" : "text-muted-foreground"
                }`}>
                  {step.label}
                </p>

                <p className="text-[11px] text-muted-foreground leading-tight">
                  {isComplete && step.id === "payment"
                    ? step.description
                    : isComplete
                    ? "Completed ✓"
                    : step.description}
                </p>

                {/* CTA for current step (not locked) */}
                {isCurrent && !isLocked && step.actionPath && step.actionLabel && (
                  <div className="mt-3 flex items-center gap-1 text-xs font-semibold text-primary group-hover:underline">
                    {step.actionLabel}
                    <ChevronRight size={14} />
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default PatientProgressBar;
